import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Building, Loader2, BarChart3, FileText, Euro, Users, ArrowRight } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function EspaceHolding() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/holding/dashboard`).then(r => setData(r.data))
      .catch(() => toast.error('Accès holding non disponible'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  if (!data) return <div className="text-center py-12 text-slate-500">Accès réservé aux comptes holding.</div>;

  const totalInvoices = data.structures?.reduce((s, st) => s + (st.total_invoices || 0), 0) || 0;
  const totalHT = data.structures?.reduce((s, st) => s + (st.total_ht || 0), 0) || 0;
  const totalUsers = data.structures?.reduce((s, st) => s + (st.user_count || 0), 0) || 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold"><Building className="w-6 h-6 inline mr-2" />Espace Holding</h1>
          <p className="text-slate-500">Vue consolidée de vos structures</p>
        </div>
        <Badge variant="outline" className="text-sm">{data.structures?.length || 0} structure(s)</Badge>
      </div>

      {/* KPIs consolidés */}
      <div className="grid grid-cols-4 gap-4">
        <Card><CardContent className="pt-6 text-center">
          <p className="text-3xl font-bold text-[#0F5156]">{data.structures?.length || 0}</p>
          <p className="text-sm text-slate-500">Structures</p>
        </CardContent></Card>
        <Card><CardContent className="pt-6 text-center">
          <p className="text-3xl font-bold">{totalInvoices}</p>
          <p className="text-sm text-slate-500">Factures totales</p>
        </CardContent></Card>
        <Card><CardContent className="pt-6 text-center">
          <p className="text-3xl font-bold text-[#0F5156]">{formatCurrency(totalHT)}</p>
          <p className="text-sm text-slate-500">Volume HT total</p>
        </CardContent></Card>
        <Card><CardContent className="pt-6 text-center">
          <p className="text-3xl font-bold">{totalUsers}</p>
          <p className="text-sm text-slate-500">Utilisateurs</p>
        </CardContent></Card>
      </div>

      {/* Structures list */}
      <Card>
        <CardHeader>
          <CardTitle>Structures du groupe</CardTitle>
          <CardDescription>Cliquez sur une structure pour accéder à son espace</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Nom</TableHead>
              <TableHead>SIRET</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead className="text-right">Factures</TableHead>
              <TableHead className="text-right">Volume HT</TableHead>
              <TableHead className="text-right">Utilisateurs</TableHead>
              <TableHead className="w-20"></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(data.structures || []).map(st => (
                <TableRow key={st.structure_id} className="cursor-pointer hover:bg-slate-50">
                  <TableCell className="font-semibold">{st.nom}</TableCell>
                  <TableCell className="font-mono text-sm text-slate-500">{st.siret}</TableCell>
                  <TableCell>
                    <Badge className={st.subscription_status === 'active' ? 'bg-green-100 text-green-700' : st.subscription_status === 'trial' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}>
                      {st.subscription_status || 'trial'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-mono">{st.total_invoices || 0}</TableCell>
                  <TableCell className="text-right font-mono">{formatCurrency(st.total_ht || 0)}</TableCell>
                  <TableCell className="text-right">{st.user_count || 0}</TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" onClick={() => toast.info(`Switch vers ${st.nom} — fonctionnalité à venir`)}>
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
